import { KeyboardResize } from '@capacitor/keyboard';
import { IonButton, IonText } from '@ionic/react';
import { Formik } from 'formik';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PinField } from 'react-pin-field';
import { useToggle } from 'react-use';
import * as yup from 'yup';
import { i18nKeyType } from '../../i18n';
import {
  setKeyboardResizeMode,
  useKeyboardDidHide,
  useKeyboardWillShow,
} from '../../theme/keyboard.hook';
import { sleep } from '../../util/async';
import { useModalDialog } from '../modal/modal-dialog';
import './user.css';

interface UserPinCodeConfirmProps {
  onPinCodeConfirm: (pinCode: string) => void;
  submitLabel?: i18nKeyType;
  dismiss: () => void;
}

interface UserPinCodeFormValues {
  pinCode: string;
}

interface UseUserPinCodeConfirm {
  present: () => void;
  dismiss: () => void;
}

const UserPinCodeConfirm = (props: UserPinCodeConfirmProps) => {
  const { onPinCodeConfirm, dismiss, submitLabel } = props;
  const { i18n } = useTranslation();

  const [showCode, toggleShowCode] = useToggle(false);

  const onSubmit = useCallback(
    (values: UserPinCodeFormValues) => {
      onPinCodeConfirm(values.pinCode);
      dismiss();
    },
    [onPinCodeConfirm, dismiss]
  );

  const initialValues = useMemo(() => {
    return {} as UserPinCodeFormValues;
  }, []);

  const formSchema = useMemo(() => {
    return yup.object().shape({
      pinCode: yup
        .string()
        .required('*')
        .matches(/^\d{4}$/, i18n.t('common:pin_code_complexity')),
    });
  }, [i18n]);

  const formSubmitLabel = useMemo(() => {
    return submitLabel ? i18n.t(submitLabel) : i18n.t('common:pin_code_set');
  }, [submitLabel, i18n]);

  const pinRef = useRef<HTMLInputElement[]>([]);

  const focusNextInput = useCallback(async (delay?: number) => {
    if (delay) {
      await sleep(delay);
    }
    const firstBlankIndex = pinRef.current.findIndex((o) => o.value === '');
    pinRef.current[firstBlankIndex].focus();
  }, []);

  useEffect(() => {
    focusNextInput(200);
  }, [focusNextInput]);

  const revertResizeMode = useRef<() => Promise<void>>();

  useKeyboardWillShow(async () => {
    revertResizeMode.current = await setKeyboardResizeMode(KeyboardResize.None);
  });

  useKeyboardDidHide(() => {
    if (revertResizeMode.current) {
      revertResizeMode.current();
    }
  });

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={formSchema}
      validateOnMount={true}
    >
      {({ setFieldValue, submitForm }) => {
        return (
          <div className="user-pin-container">
            <div className="full-height-width content-center">
              <IonText class="ion-text-center">{formSubmitLabel}</IonText>
              <br />
            </div>
            <PinField
              className="pin-field"
              ref={pinRef}
              inputMode="numeric"
              length={4}
              validate={/^[0-9]$/}
              type={showCode ? undefined : 'password'}
              onChange={async (code) => {
                await setFieldValue('pinCode', code);
              }}
              onClick={() => {
                // this handles the case where the user clicks on a pin field
                // and there is a blank field preceeding the one clicked
                focusNextInput();
              }}
              onComplete={async (code) => {
                // we need to set and await the final value so that formik has the
                // full pin before we call submit form
                await setFieldValue('pinCode', code);
                submitForm();
              }}
            />
            <br />
            <div className="pin-button-wrapper">
              <IonButton
                className="ion-no-padding"
                fill="clear"
                onClick={dismiss}
              >
                {i18n.t('common:cancel')}
              </IonButton>
              <IonButton
                className="ion-no-padding"
                fill="clear"
                onClick={() => {
                  focusNextInput();
                  toggleShowCode();
                }}
              >
                {showCode
                  ? i18n.t('common:pin_code_hide')
                  : i18n.t('common:pin_code_show')}
              </IonButton>
            </div>
          </div>
        );
      }}
    </Formik>
  );
};

const useUserPinCodeConfirmation = (
  props: Omit<UserPinCodeConfirmProps, 'dismiss'>
): UseUserPinCodeConfirm => {
  const { present, dismiss } = useModalDialog(UserPinCodeConfirm, {
    ...props,
    dismiss: () => {
      dismiss();
    },
  });

  const result = useMemo<UseUserPinCodeConfirm>(() => {
    return {
      present: () => {
        present({
          backdropDismiss: false,
        });
      },
      dismiss,
    };
  }, [present, dismiss]);

  return result;
};

export { useUserPinCodeConfirmation };
